import React, { useMemo } from "react";
import sortBy from "lodash/sortBy";
import * as Yup from "yup";
import { Formik } from "formik";
import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import Input from "../../form/Input";
import Select from "../../form/Select";
import useAuth from "../../../hooks/useAuth";
import FullscreenModal from "../../modals/fullscreen-modal";
import MovewareStorageAccountField from "./MovewareStorageAccountField";
import { CLIENTS_AND_PROJECTS_QUERY } from "../../../constants/graphql/queries";

export default function DuplicateProjectModal({ initialValues, onSubmit, onClose }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const movewareEnabled = user.company.enabledIntegrationProviders?.includes("moveware");
  const { data: { clients = [] } = {}, loading } = useQuery(CLIENTS_AND_PROJECTS_QUERY);

  // Get all active projects across all clients - sorted alphabetically.
  // A project shared with several clients comes back once per client, so
  // de-duplicate by id. Distinct projects can also share a name, so the
  // Autocomplete below keys options by id (MUI defaults to the label, and
  // duplicate React keys leave stale rows in the listbox when typing filters it).
  const allProjects = useMemo(() => {
    const projectsById = new Map();
    clients.forEach(client => {
      client.projects.forEach(project => {
        if (project.active && !projectsById.has(project.id)) {
          projectsById.set(project.id, {
            id: project.id,
            name: project.name,
            label: project.name, // For autocomplete display
            value: project.id, // For autocomplete value
            clientName: client.name,
          });
        }
      });
    });
    return sortBy([...projectsById.values()], p => (p?.name || "").toLowerCase());
  }, [clients]);

  const assignableClients = useMemo(
    () =>
      clients.filter(
        client => !client.disabled && user.clientIds.includes(client.id)
      ),
    [clients, user.clientIds]
  );

  // Get all clients for autocomplete - sorted alphabetically
  const allClients = useMemo(() => {
    return sortBy(
      assignableClients.map(client => ({
        id: client.id,
        name: client.name,
        label: client.name,
        value: client.id,
      })),
      c => (c?.name || "").toLowerCase()
    );
  }, [assignableClients]);

  const validationSchema = Yup.object().shape({
    clientId: Yup.string().required(`${settings.filter_field1} required`),
    newProjectName: Yup.string().required(`${settings.filter_field2} name required`),
    sourceProjectId: Yup.string().required("Source project required"),
    ...(movewareEnabled && {
      movewareStorageAccountId: Yup.string()
        .trim()
        .matches(/^\d+$/, "Moveware Storage Account ID must contain only digits")
        .required("Moveware Storage Account ID required"),
      externalRecordId: Yup.string().required("Look up and confirm the Moveware storage account"),
    }),
  });

  const formikOnSubmit = (values, { setErrors, setStatus, setSubmitting }) => {
    setSubmitting(true);
    const variables = {
      sourceProjectId: values.sourceProjectId,
      newProjectName: values.newProjectName,
      clientId: values.clientId,
      ...(values.externalRecordId ? { externalRecordId: values.externalRecordId } : {}),
    };
    onSubmit({
      variables,
      onSuccess: () => {
        setSubmitting(false);
        setStatus({ success: true });
        onClose();
      },
      onFailure: errors => {
        setStatus({ success: false });
        setErrors(errors);
        setSubmitting(false);
      },
    });
  };

  return (
    <Formik
      initialValues={{
        ...initialValues,
        movewareStorageAccountId: "",
        externalRecordId: "",
      }}
      validationSchema={validationSchema}
      onSubmit={formikOnSubmit}
    >
      {({ errors, handleBlur, isSubmitting, handleSubmit, values, setFieldValue, touched }) => {
        const selectedProject = allProjects.find(p => p.id === values.sourceProjectId) || null;
        const selectedClient = allClients.find(c => c.id === values.clientId) || null;

        return (
          <form onSubmit={handleSubmit}>
            <FullscreenModal
              open
              id="duplicate-project"
              title={`Duplicate ${settings.filter_field2}`}
              titleFontSize="1.5rem"
              maxWidth={600}
              onClose={onClose}
              disableFullScreen
              actions={[
                <Button
                  key="duplicate"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || (movewareEnabled && !values.externalRecordId)}
                  type="submit"
                  onClick={handleSubmit}
                >
                  Duplicate {settings.filter_field2}
                </Button>,
              ]}
            >
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {movewareEnabled && <MovewareStorageAccountField clients={assignableClients} />}
                <Grid size={12}>
                  <Autocomplete
                    autoFocus
                    options={allClients}
                    getOptionKey={option => option.id}
                    getOptionLabel={option => option.label || ""}
                    value={selectedClient}
                    onChange={(event, newValue) => {
                      setFieldValue("clientId", newValue ? newValue.id : "");
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    ListboxProps={{
                      style: {
                        maxHeight: "300px",
                      },
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={`Assign to ${settings.filter_field1}`}
                        variant="outlined"
                        size="small"
                        error={touched.clientId && !!errors.clientId}
                        helperText={touched.clientId && errors.clientId}
                      />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Input
                    name="newProjectName"
                    label={`New ${settings.filter_field2} Name`}
                    autoComplete="off"
                    showNAButton={false}
                  />
                </Grid>
                <Grid size={12}>
                  <Autocomplete
                    options={allProjects}
                    getOptionKey={option => option.id}
                    getOptionLabel={option => option.label || ""}
                    value={selectedProject}
                    onChange={(event, newValue) => {
                      setFieldValue("sourceProjectId", newValue ? newValue.id : "");
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    ListboxProps={{
                      style: {
                        maxHeight: "300px",
                      },
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={`Copy from ${settings.filter_field2}`}
                        variant="outlined"
                        size="small"
                        error={touched.sourceProjectId && !!errors.sourceProjectId}
                        helperText={touched.sourceProjectId && errors.sourceProjectId}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              {errors.submit && (
                <Box mt={2}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Box>
              )}
            </FullscreenModal>
          </form>
        );
      }}
    </Formik>
  );
}
